/**
 * Structured integration guide data for all LMS types.
 * Used by both the frontend help page and backend PDF generation.
 *
 * Format: each step has a heading and markdown-like body content.
 * Lists are separated by \n. Inline code with backticks.
 */

export type LmsType = 'moodle' | 'tutorlms' | 'learndash' | 'xtramile';

export interface GuideStep {
  title: string;
  body: string;
  note?: string;
}

export interface GuideError {
  error: string;
  cause: string;
  solution: string;
}

export interface IntegrationGuide {
  lmsType: LmsType;
  displayName: string;
  tagline: string;
  description: string;
  estimatedTime: string;
  prerequisites: string[];
  steps: GuideStep[];
  requiredFunctions?: { name: string; description: string; required: boolean }[];
  commonErrors: GuideError[];
  securityTips: string[];
  officialDocsUrl: string;
  officialDocsLabel: string;
}

const OUR_IP = '5.249.254.24';

// Fase 2.6: delte feilkoder som rammer alle LMS-mappinger når studieplanens
// vurderingsramme håndheves ved kobling. Se help_articles 'lms-framework-check'
// og 'lms-signoff-when-required' for utdypende forklaring.
const FRAMEWORK_ERRORS: GuideError[] = [
  {
    error: '409 framework_blocking (strict)',
    cause: 'Studieplanens vurderingsramme har flexibility=strict, og valgt vurderingstype matcher ikke primary_method_class.',
    solution: 'Velg en annen vurderingstype som matcher rammen, ELLER få studieplan-forfatter til å endre rammen til equivalent_allowed. Rammen beskytter mot at kurs blir vurdert med metoder som ikke passer læringsutbyttene.'
  },
  {
    error: '422 framework_rationale_required (equivalent_allowed)',
    cause: 'Rammen tillater avvik, men begrunnelse (framework_rationale) må være minst 50 tegn.',
    solution: 'Skriv en konkret pedagogisk begrunnelse i mapping-modal — f.eks. hvorfor denne vurderingstypen gir likeverdig måling av læringsutbyttene. Begrunnelsen vises på attesten.'
  },
  {
    error: 'rubric_mismatch',
    cause: 'Rammen krever rubric (rubric_required=true) og mapping må bekrefte at LMS-kurset har en vurderingsmatrise.',
    solution: 'Hak av "LMS-kurset har rubric eller strukturert vurderingsmatrise" i mapping-modal, ELLER bruk vårt eksamenssystem med egen rubric.'
  },
  {
    error: 'signoff_required (Xtramile/MentorKit)',
    cause: 'Disse LMS-ene har ikke capability-detektorer. Admin må aktivt bekrefte at vurderingen oppfyller rammens krav.',
    solution: 'Som admin: gå gjennom bekreftelses-sjekkbokser i mapping-modal. Signoff logges i lms_mapping_signoff-tabellen med din user_id, tidspunkt og hva du bekreftet.'
  }
];

export const INTEGRATION_GUIDES: Record<LmsType, IntegrationGuide> = {
  moodle: {
    lmsType: 'moodle',
    displayName: 'Moodle',
    tagline: 'Open source LMS',
    description:
      'Moodle bruker Web Services med REST-protokoll. Vi henter kurs, påmeldinger, fullføringsstatus og karakterdata.',
    estimatedTime: '10–15 minutter',
    prerequisites: [
      'Moodle 3.5 eller nyere (anbefalt 4.x)',
      'Du må være Moodle-administrator',
      'Moodle må være tilgjengelig fra internett via HTTPS',
      `Hvis Moodle har IP-hvitliste: legg til ${OUR_IP}`
    ],
    steps: [
      {
        title: 'Slå på Web Services globalt',
        body:
          '1. Gå til **Site administration → Advanced features**\n' +
          '2. Sett "Enable web services" til **Yes**\n' +
          '3. Klikk **Save changes**'
      },
      {
        title: 'Aktiver REST-protokollen',
        body:
          '1. **Site administration → Plugins → Web services → Manage protocols**\n' +
          '2. Klikk øye-ikonet ved "REST protocol" slik at den blir aktivert',
        note: 'Ikke aktiver SOAP eller XML-RPC (deprecated).'
      },
      {
        title: 'Opprett en dedikert bruker for integrasjonen',
        body:
          '1. **Site administration → Users → Accounts → Add a new user**\n' +
          '2. Username: `kompetanseattest_api`\n' +
          '3. Sett et sterkt passord (brukeren logger aldri inn direkte)\n' +
          '4. Fyll inn fornavn, etternavn og en gyldig e-post',
        note:
          'Bruk IKKE din personlige admin-konto. Dedikert bruker gjør det enkelt å trekke tilbake tilgang senere.'
      },
      {
        title: 'Gi brukeren systemrolle (enkleste og mest pålitelige løsning)',
        body:
          'Du har to alternativer her. **Vi anbefaler Alternativ A** fordi Moodles per-kurs-tilgangskontroll ofte gjør Alternativ B upålitelig.\n\n' +
          '**Alternativ A: Tildel Manager-rollen i systemkontekst (anbefalt)**\n' +
          '1. **Site administration → Users → Permissions → Assign system roles**\n' +
          '2. Velg rollen **Manager**\n' +
          '3. Finn `kompetanseattest_api` i høyre liste → klikk **Add**\n' +
          '4. Ferdig — denne rollen har innebygd alt vi trenger\n\n' +
          '**Alternativ B: Custom rolle (mer restriktiv, krever ekstra arbeid)**\n' +
          '1. **Site administration → Users → Permissions → Define roles → Add a new role**\n' +
          '2. Name: `Kompetanseattest Web Service`\n' +
          '3. Short name: `ws_kompetanseattest`\n' +
          '4. Context types: kryss av **System** OG **Course**\n' +
          '5. Aktiver disse rettighetene (sett alle til "Allow"):\n' +
          '   - `webservice/rest:use`\n' +
          '   - `moodle/site:viewfullnames`\n' +
          '   - `moodle/user:viewdetails`\n' +
          '   - `moodle/user:viewhiddendetails`\n' +
          '   - `moodle/user:viewalldetails`\n' +
          '   - `moodle/course:view`\n' +
          '   - `moodle/course:viewhiddencourses`\n' +
          '   - `moodle/course:viewhiddenuserfields`\n' +
          '   - `moodle/course:viewparticipants`\n' +
          '   - `moodle/grade:viewall`\n' +
          '   - `moodle/grade:view` (course-nivå)\n' +
          '6. Klikk **Create this role**\n' +
          '7. **Assign system roles** → velg rollen → legg til `kompetanseattest_api`\n' +
          '8. **VIKTIG:** Selv med dette må brukeren også være meldt på (enrolled) hvert kurs som skal kobles til en studieplan, ELLER kurset må ikke være satt som skjult/begrenset. Manager-rollen (Alt. A) omgår dette.',
        note: 'Hvis du får feilmeldingen "Unntak fra tilgangskontroll" eller "Kurs eller aktivitet ikke tilgjengelig" når du prøver å koble et kurs, betyr det at WS-brukeren mangler tilgang til det aktuelle kurset. Bytt da til Alternativ A.'
      },
      {
        title: 'Opprett den eksterne tjenesten',
        body:
          '1. **Site administration → Plugins → Web services → External services → Add**\n' +
          '2. Name: `Kompetanseattest`\n' +
          '3. Short name: `kompetanseattest`\n' +
          '4. Enabled: ✓ Yes\n' +
          '5. Authorised users only: ✓ **Yes** (viktig for sikkerhet)\n' +
          '6. Klikk **Add service**'
      },
      {
        title: 'Legg til funksjoner i tjenesten',
        body:
          'Klikk **Functions**-lenken ved tjenesten, så **Add functions**. Se listen under.'
      },
      {
        title: 'Autoriser brukeren for tjenesten',
        body:
          '1. Klikk **Authorised users** på tjenesten\n' +
          '2. Velg `kompetanseattest_api` fra høyre kolonne og klikk **Add**'
      },
      {
        title: 'Opprett token',
        body:
          '1. **Site administration → Plugins → Web services → Manage tokens → Create token**\n' +
          '2. User: `kompetanseattest_api`\n' +
          '3. Service: `Kompetanseattest`\n' +
          `4. IP restriction: \`${OUR_IP}\` (anbefalt)\n` +
          '5. Valid until: la stå tom eller sett 1 år frem i tid\n' +
          '6. Klikk **Save changes**\n' +
          '7. **Kopier token-verdien umiddelbart** – den vises kun én gang'
      },
      {
        title: 'Lim inn i Kompetanseattest',
        body:
          '1. **Moodle URL:** `https://din-moodle.no` (uten skråstrek, uten /login e.l.)\n' +
          '2. **API-token:** verdien fra forrige steg\n' +
          '3. Klikk **Test tilkobling**'
      },
      {
        title: 'Konfigurer course completion på kursene som skal utstede attest',
        body:
          'Dette er **kritisk** — uten course completion blir aldri en attest utstedt. ' +
          'At en kandidat har fått 95% på en quiz er IKKE nok i seg selv. ' +
          'Moodle må eksplisitt være konfigurert til å markere kurset som "completed" basert på et eller flere kriterier.\n\n' +
          '**A. Aktiver completion tracking på kurset:**\n' +
          '1. Gå til kurset → **Course administration → Edit settings**\n' +
          '2. Under **Completion tracking**: sett til **Enabled, control via completion and activity settings**\n' +
          '3. Lagre\n\n' +
          '**B. Sett opp completion-betingelser:**\n' +
          '1. Gå til kurset → **Course administration → Course completion**\n' +
          '2. Velg ett eller flere kriterier som må oppfylles. De vanligste:\n' +
          '   - **Activity completion** — bruker må fullføre spesifikke aktiviteter (anbefalt: quiz)\n' +
          '   - **Course grade** — kursets totale karakter må være over en terskel (f.eks. 80%)\n' +
          '   - **Manual completion by other** — lærer huker av (egnet ved manuell vurdering)\n' +
          '   - **Manual completion by self** (IKKE anbefalt for attester)\n' +
          '3. For typisk quiz-flyt: kombiner **Activity completion på quizen** med karakterterskel\n\n' +
          '**C. Sett opp activity completion på quizen:**\n' +
          '1. Klikk på quizen → **Edit settings** → seksjon **Activity completion**\n' +
          '2. Velg **Show activity as complete when conditions are met**\n' +
          '3. Hak av **Require grade** og sett **Passing grade** (f.eks. ≥80% av maks)\n' +
          '4. Lagre\n\n' +
          '**D. Verifiser at oppsettet virker:**\n' +
          '- Gå til kurset → **Reports → Course completion**\n' +
          '- Sjekk at fullført-status (✓) vises for brukere som har oppnådd kriteriene\n' +
          '- Hvis kolonnen er tom eller halvferdig: kriteriene er ikke konfigurert riktig\n\n' +
          '**E. Krav til kandidatens brukerkonto:**\n' +
          '- Rolle må være **Student** (ikke Teacher/Manager — disse hoppes over av completion-tracking i Moodle)\n' +
          '- Enrolment-status må være **Active** (ikke Suspended)\n' +
          '- **Mobilnummer må være registrert** på profilen (under Optional → Phone, eller felt konfigurert i AOFs Moodle). Vi bruker mobil for å matche kandidaten mot Vipps. Du kan endre hvilket felt vi leser under Innstillinger → Moodle → "Felt for mobilnummer".\n\n' +
          '**Hva som så skjer automatisk:**\n' +
          'Vår sync kjører hvert 5. minutt. Når Moodle markerer kurset som "completed":\n' +
          '1. Vi oppretter en pending-completion i vår database\n' +
          '2. Kandidaten får SMS + e-post med lenke til kandidatportalen\n' +
          '3. Etter Vipps-innlogging og navne-/mobil-matching: attesten blir tilgjengelig'
      }
    ],
    requiredFunctions: [
      {
        name: 'core_webservice_get_site_info',
        description: 'Tilkoblingstest og versjonssjekk (typisk automatisk tilgjengelig)',
        required: true
      },
      {
        name: 'core_course_get_courses_by_field',
        description: 'Liste over kurs respektive WS-brukerens tilganger (primær API)',
        required: true
      },
      {
        name: 'core_course_get_courses',
        description: 'Fallback for eldre Moodle-versjoner som mangler _by_field',
        required: false
      },
      {
        name: 'core_enrol_get_enrolled_users',
        description: 'Deltakere per kurs (kandidater)',
        required: true
      },
      {
        name: 'core_completion_get_course_completion_status',
        description: 'Fullføringsstatus per kandidat',
        required: true
      },
      {
        name: 'core_user_get_users_by_field',
        description: 'Brukerinfo (e-post, mobil) for identitetsmatching',
        required: true
      },
      {
        name: 'gradereport_user_get_grade_items',
        description: 'Karakter/poeng som tas med på kompetanseattesten',
        required: false
      }
    ],
    commonErrors: [
      ...FRAMEWORK_ERRORS,
      {
        error: 'Invalid token',
        cause: 'Token feil eller utløpt',
        solution: 'Generer ny token (steg 8)'
      },
      {
        error: 'Access denied',
        cause: 'Brukeren mangler capability webservice/rest:use',
        solution: 'Se steg 4 – legg til rettigheten i rollen, eller bytt til Manager-rollen (Alternativ A)'
      },
      {
        error: 'Unntak fra tilgangskontroll / Access control exception',
        cause: 'WS-brukeren har ikke tilgang til det spesifikke kurset (course visibility eller enrollment)',
        solution: 'Bytt til Alternativ A i steg 4 (gi brukeren Manager-rollen i systemkontekst), eller meld brukeren på hvert relevant kurs som "Manager"'
      },
      {
        error: 'Kurs eller aktivitet ikke tilgjengelig / Course or activity unavailable',
        cause: 'Custom-rollen mangler permissions på course-context, eller kurset er skjult',
        solution: 'Bruk Manager-rollen (Alt. A), eller gjør kurset synlig og meld brukeren på kurset'
      },
      {
        error: 'Function not found',
        cause: 'Funksjon mangler i tjenesten',
        solution: 'Se steg 6 og legg til funksjonen – inkludér core_course_get_courses_by_field'
      },
      {
        error: 'Invalid response / HTML tilbake',
        cause: 'Moodle-URL peker på /login eller tilsvarende',
        solution: 'Bruk kun base-URL uten sti'
      },
      {
        error: 'IP not allowed',
        cause: 'Token har IP-restriksjon som ikke matcher',
        solution: `Sett IP til ${OUR_IP}`
      },
      {
        error: 'Kandidat har bestått quiz med høy karakter, men får ikke attest',
        cause: 'Course completion er ikke konfigurert på kurset. Quiz-karakter alene utløser ikke "completed"-status.',
        solution: 'Følg steg 10 i veiledningen — aktiver completion tracking på kurset og sett opp completion-betingelser (Activity completion på quiz + eventuell karakterterskel). Verifiser i Reports → Course completion at fullført-status faktisk vises.'
      },
      {
        error: 'Brukeren er ikke påmeldt (notenrolled) ved completion-sjekk',
        cause: 'Selv om brukeren er enrolled i kurset, er rollen Teacher/Manager (ikke Student), eller enrolment-status er Suspended. Disse er ikke "trackable" for completion-tracking.',
        solution: 'Endre rollen til Student i kursets Participants-liste, og deaktiver eventuelle Suspended-flagg på enrolment. Hvis brukeren ER lærer (ikke kandidat), skal de hoppes over — sync ignorerer dem automatisk uten å feile.'
      },
      {
        error: 'Attest utstedes ikke selv om Reports → Course completion viser ✓',
        cause: 'Brukeren mangler mobilnummer på Moodle-profilen, eller mobilfeltet konfigurert i Kompetanseattest peker på feil felt.',
        solution: 'Sjekk at brukerens Moodle-profil har telefon registrert. Hvis ikke: be brukeren legge til. I Kompetanseattest: Innstillinger → Moodle → "Felt for mobilnummer" — sett til det Moodle-feltet du faktisk lagrer mobil i (typisk "phone1" eller "phone2").'
      }
    ],
    securityTips: [
      `Bruk alltid IP-restriksjon på token (\`${OUR_IP}\`)`,
      'Bruk dedikert brukerkonto, ikke din personlige admin-konto',
      'Rollen i steg 4 gir kun det som trengs – ikke bredere',
      'Sett utløpsdato på token og fornye periodisk (f.eks. årlig)',
      'Moodle logger alle API-kall under Site administration → Reports → Logs → Web services',
      'Deaktiver brukeren for umiddelbar tilbakekalling av all tilgang'
    ],
    officialDocsUrl: 'https://docs.moodle.org/en/Using_web_services',
    officialDocsLabel: 'Moodle Web Services-dokumentasjon'
  },

  tutorlms: {
    lmsType: 'tutorlms',
    displayName: 'TutorLMS',
    tagline: 'WordPress-basert LMS (krever TutorLMS Pro)',
    description:
      'TutorLMS har sitt eget Rest API-system med Client ID/Secret (IKKE WordPress Application Password). For karakter-henting kreves TutorLMS Pro.',
    estimatedTime: '5 minutter',
    prerequisites: [
      'TutorLMS Pro installert og aktivert (påkrevd for kursfullføringsdata via API)',
      'WordPress-administrator-tilgang',
      'WordPress-nettstedet må være tilgjengelig over HTTPS'
    ],
    steps: [
      {
        title: 'Logg inn som WordPress-administrator',
        body: 'Gå til WordPress Admin-panelet på din TutorLMS-installasjon.'
      },
      {
        title: 'Gå til TutorLMS Rest API-panelet',
        body:
          '1. I venstre meny: **TutorLMS → Tools → Rest API**\n' +
          '2. I noen versjoner ligger dette under **TutorLMS → Rest API** direkte'
      },
      {
        title: 'Opprett et nytt API-nøkkelpar',
        body:
          '1. Klikk **"Legg til ny"** (Add New)\n' +
          '2. Gi nøkkelen et navn, f.eks. `Kompetanseattest`\n' +
          '3. Sett **Tillatelse** til **"All"** (eller minst **Read/Write**)\n' +
          '4. Klikk **Opprett**'
      },
      {
        title: 'Kopier Client ID og Client Secret',
        body:
          'Etter opprettelse vises to verdier:\n' +
          '- **Client ID** (API Key): starter med `key_...`\n' +
          '- **Client Secret** (Hemlig): starter med `secret_...`\n\n' +
          '**Kopier begge umiddelbart** – Secret vises kun én gang og kan ikke hentes igjen senere.',
        note:
          'VIKTIG: Dette er IKKE WordPress Application Password. TutorLMS har sitt eget API-system som er helt atskilt.'
      },
      {
        title: 'Lim inn i Kompetanseattest',
        body:
          '1. **WordPress URL:** `https://din-side.no` (base-URL uten /wp-admin)\n' +
          '2. **Client ID:** hele `key_...`-verdien\n' +
          '3. **Client Secret:** hele `secret_...`-verdien\n' +
          '4. Klikk **Test tilkobling**'
      }
    ],
    commonErrors: [
      ...FRAMEWORK_ERRORS,
      {
        error: '401 Unauthorized / rest_forbidden',
        cause: 'Feil Client ID eller Secret, eller bruk av WordPress Application Password i stedet for TutorLMS-nøkler',
        solution: 'Verifiser at verdier starter med key_ og secret_ (ikke vanlig passord). Opprett nytt par om nødvendig.'
      },
      {
        error: '403 Forbidden / Access denied',
        cause: 'TutorLMS Rest API er ikke aktivert, eller manglende TutorLMS Pro',
        solution: 'Bekreft at TutorLMS Pro er installert og Rest API er aktivert.'
      },
      {
        error: '400 Invalid input på /enrollments',
        cause: 'Forventet oppførsel – bekrefter at Pro er installert',
        solution: 'Ingen handling kreves – håndteres automatisk av vår klient.'
      },
      {
        error: 'HTML tilbake i stedet for JSON',
        cause: 'WordPress URL er feil eller peker på et underdomene uten REST API',
        solution: 'Sjekk at base-URL er riktig og at /wp-json returnerer JSON.'
      }
    ],
    securityTips: [
      'Dedikerte API-nøkler for hver tredjepartsintegrasjon (ikke del på tvers)',
      'Bruk "All"- eller "Read/Write"-tillatelse, ikke bredere',
      'Hvis dere senere vil revokere tilgang: slett nøkkelparet i TutorLMS Rest API-menyen',
      'Secret-verdien kan ikke hentes tilbake – må opprettes på nytt hvis den er mistet'
    ],
    officialDocsUrl: 'https://docs.themeum.com/tutor-lms/developer-documentation/rest-apis-for-tutor-lms-pro/',
    officialDocsLabel: 'TutorLMS Pro REST API-dokumentasjon'
  },

  learndash: {
    lmsType: 'learndash',
    displayName: 'LearnDash',
    tagline: 'WordPress-basert LMS (REST API i alle lisenser)',
    description:
      'LearnDash bruker WordPress sin standard REST API v2. Autentisering skjer via WordPress Application Passwords.',
    estimatedTime: '5 minutter',
    prerequisites: [
      'LearnDash (REST API v2 er tilgjengelig i alle lisensplaner)',
      'WordPress 5.6 eller nyere (for Application Passwords)',
      'WordPress-administrator-tilgang',
      'WordPress-nettstedet må være tilgjengelig over HTTPS'
    ],
    steps: [
      {
        title: 'Logg inn som WordPress-administrator',
        body: 'Gå til WordPress Admin-panelet på din LearnDash-installasjon.'
      },
      {
        title: 'Opprett eller identifiser en dedikert bruker',
        body:
          'Det anbefales å opprette en dedikert WordPress-bruker for integrasjonen, i stedet for å bruke personlig admin-konto:\n\n' +
          '1. **Brukere → Legg til ny**\n' +
          '2. Brukernavn: `kompetanseattest_api`\n' +
          '3. E-post: en teknisk e-postadresse\n' +
          '4. Rolle: **Administrator** (LearnDash REST API krever administrator-tilgang)\n' +
          '5. Klikk **Legg til ny bruker**'
      },
      {
        title: 'Åpne Application Passwords for brukeren',
        body:
          '1. **Brukere → Alle brukere** → klikk brukeren\n' +
          '2. Scroll ned til seksjonen **"Application Passwords"**\n' +
          '3. Hvis seksjonen mangler: WordPress-versjonen må være 5.6+ og Application Passwords må ikke være deaktivert via filter'
      },
      {
        title: 'Generer et Application Password',
        body:
          '1. Under **"New Application Password Name"**, skriv `Kompetanseattest`\n' +
          '2. Klikk **"Add New Application Password"**\n' +
          '3. WordPress viser passordet i format `xxxx xxxx xxxx xxxx xxxx xxxx` (24 tegn i 6 grupper)\n' +
          '4. **Kopier hele passordet umiddelbart** – det vises kun én gang'
      },
      {
        title: 'Lim inn i Kompetanseattest',
        body:
          '1. **WordPress URL:** `https://din-side.no` (base-URL uten /wp-admin)\n' +
          '2. **Brukernavn:** `kompetanseattest_api` (WordPress-brukernavnet)\n' +
          '3. **Application Password:** det genererte passordet (mellomrom er OK)\n' +
          '4. Klikk **Test tilkobling**'
      }
    ],
    commonErrors: [
      ...FRAMEWORK_ERRORS,
      {
        error: '401 Unauthorized',
        cause: 'Feil brukernavn eller Application Password',
        solution: 'Verifiser brukernavn. Generer nytt Application Password om nødvendig.'
      },
      {
        error: '403 Forbidden',
        cause: 'Brukeren har ikke administrator-rolle',
        solution: 'Gi brukeren Administrator-rolle. LearnDash REST API krever dette.'
      },
      {
        error: 'Application Passwords-seksjonen mangler',
        cause: 'WordPress under 5.6 eller deaktivert via filter/plugin',
        solution: 'Oppgrader WordPress. Sjekk at ingen sikkerhetsplugin blokkerer Application Passwords.'
      },
      {
        error: 'rest_no_route / 404 på endpoints',
        cause: 'LearnDash REST API ikke aktivert i innstillinger',
        solution: 'Gå til **LearnDash LMS → Innstillinger → REST API** og aktiver.'
      }
    ],
    securityTips: [
      'Bruk dedikert WordPress-bruker (ikke personlig admin)',
      'Application Passwords kan revokeres fra brukerprofilen uten å påvirke passordet til brukeren',
      'Navngiving på password ("Kompetanseattest") gir audit-trail – se når det ble brukt',
      'Hvis dere mistenker lekkasje: revoker og generer nytt'
    ],
    officialDocsUrl: 'https://developers.learndash.com/rest-api/v2/',
    officialDocsLabel: 'LearnDash REST API v2-dokumentasjon'
  },

  xtramile: {
    lmsType: 'xtramile',
    displayName: 'Xtramile',
    tagline: 'Norsk e-læringsplattform',
    description:
      'Xtramile bruker et proprietært API med tenant-spesifikk URL og API-nøkkel. Kontakt Xtramile support for å få tilgang hvis din organisasjon ikke allerede har API aktivert.',
    estimatedTime: '5–10 minutter',
    prerequisites: [
      'Aktiv Xtramile-konto for din organisasjon',
      'API-tilgang må være aktivert (kontakt Xtramile support om nødvendig)',
      'Administrator-tilgang i Xtramile-portalen'
    ],
    steps: [
      {
        title: 'Logg inn i Xtramile-administrasjonspanelet',
        body: 'Gå til administrasjonspanelet for din Xtramile-instans.'
      },
      {
        title: 'Finn API-innstillinger',
        body:
          '1. Naviger til **Innstillinger → API** (nøyaktig plassering kan variere)\n' +
          '2. Hvis ingen API-seksjon vises: kontakt Xtramile support og be dem aktivere API-tilgang for din organisasjon'
      },
      {
        title: 'Generer eller hent API-nøkkel',
        body:
          '1. Klikk **"Generer ny API-nøkkel"** (eller tilsvarende)\n' +
          '2. Navngi nøkkelen: `Kompetanseattest`\n' +
          '3. **Kopier API-nøkkelen** umiddelbart – vises ofte kun én gang'
      },
      {
        title: 'Noter tenant-URL',
        body:
          'Xtramile har tenant-spesifikk URL, typisk i formatet:\n' +
          '- `https://din-organisasjon.xtramile.no`\n' +
          '- eller `https://xtramile.no/din-organisasjon`\n\n' +
          'Bekreft korrekt URL med Xtramile hvis dere er usikre.'
      },
      {
        title: 'Lim inn i Kompetanseattest',
        body:
          '1. **Tenant URL / Base URL:** som noteret over\n' +
          '2. **API-nøkkel:** verdien du kopierte\n' +
          '3. Klikk **Test tilkobling**'
      }
    ],
    commonErrors: [
      ...FRAMEWORK_ERRORS,
      {
        error: '401 Unauthorized / Invalid API key',
        cause: 'Feil eller utløpt API-nøkkel',
        solution: 'Generer ny nøkkel og lim inn på nytt.'
      },
      {
        error: '403 Forbidden',
        cause: 'API-nøkkelen har ikke tilstrekkelige tillatelser',
        solution: 'Kontakt Xtramile support for å utvide tillatelsene.'
      },
      {
        error: '404 Not Found',
        cause: 'Feil tenant-URL',
        solution: 'Verifiser korrekt URL med Xtramile.'
      },
      {
        error: 'Tilkobling timeout',
        cause: 'Xtramile-instans ikke tilgjengelig fra internett',
        solution: 'Kontakt Xtramile om tilkoblingsproblemer.'
      }
    ],
    securityTips: [
      'Bruk dedikert API-nøkkel per integrasjon',
      'Revoker nøkler dere ikke lenger bruker',
      'Ikke del API-nøkkelen på e-post eller chat – bruk sikker kanal'
    ],
    officialDocsUrl: 'https://xtramile.no',
    officialDocsLabel: 'Xtramile'
  }
};
